import { FC, useState } from 'react';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, IconButton, Paper, Typography } from '@mui/material';
import { useDoctorClips } from 'client/hooks/doctors/useDoctorClips';
import { IProcedureVideoClip } from 'common/types';
import { formatVideoLengthTime } from 'common/utils/helper';
import VideoPreviewModal from '../VideoPreviewModal';

interface IClipGalleryProps {
    doctorId: number;
}

export const ClipGallery: FC<IClipGalleryProps> = ({ doctorId }) => {
    const { data: clips, isLoading } = useDoctorClips(doctorId);
    const [isVideoOpen, setIsVideoOpen] = useState(false);
    const [openClip, setOpenClip] = useState<IProcedureVideoClip | null>(null);

    const showClips = !isLoading && clips && clips.length > 0;

    if (!showClips) {
        return null;
    }

    const onViewClip = (clip: IProcedureVideoClip) => {
        setOpenClip(clip);
        setIsVideoOpen(true);
    };

    const onCloseVideoClip = () => {
        setIsVideoOpen(false);
        setOpenClip(null);
    };

    return (
        <>
            <Box sx={{ flexGrow: 1, padding: 1, borderTop: '1px solid #ddd', paddingTop: '10px' }}>
                <Typography variant="body1" style={{ color: '#111', fontWeight: 'bold' }} textAlign="center">
                    Latest Clips
                </Typography>
                <Box gap={1} display="grid" gridTemplateColumns="repeat(3, 1fr)" sx={{ marginTop: '5px' }}>
                    {clips.map(clip => (
                        <Paper key={clip.clipId} sx={{ display: 'flex', flexDirection: 'column', borderRadius: '7px' }}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    backgroundColor: '#f5f5f5',
                                    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgb(26, 33, 35)), url(https://vsurgic.com/wp-content/uploads/2024/05/vsurgic-installation-min.png)`,
                                    backgroundPosition: 'bottom',
                                    backgroundSize: 'cover',
                                    height: '130px',
                                    display: 'flex',
                                    borderTopLeftRadius: '7px',
                                    borderTopRightRadius: '7px',
                                }}
                            >
                                <IconButton
                                    onClick={() => onViewClip(clip)}
                                    sx={{ justifyContent: 'center', alignItems: 'center', color: '#fff', width: '100%', borderRadius: 0 }}
                                >
                                    <Box sx={{ padding: '1px 4px', borderRadius: '50%', background: 'rgba(255, 255, 255, 0.25)' }}>
                                        <PlayArrowIcon fontSize="small" />
                                    </Box>
                                </IconButton>
                            </Box>
                            <Box sx={{ padding: '10px 7px', display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body1" style={{ color: '#111', fontWeight: 'bold', fontSize: '0.7rem' }}>
                                    {clip.title}
                                </Typography>
                                <Typography variant="body1" style={{ color: '#888', fontSize: '0.7rem' }}>
                                    {formatVideoLengthTime(clip.videoLength)}
                                </Typography>
                            </Box>
                        </Paper>
                    ))}
                </Box>
            </Box>
            {isVideoOpen && openClip && (
                <VideoPreviewModal
                    setStartTime={() => null}
                    setEndTime={() => null}
                    setCaptureTime={() => null}
                    setIsOpen={() => null}
                    setTempStartTime={() => null}
                    setTempEndTime={() => null}
                    handleOpenClose={onCloseVideoClip}
                    setTitle={() => null}
                    isOpen={isVideoOpen}
                    exporting={false}
                    presignedUrl={openClip.streamLocation}
                    tempStartTime=""
                    tempEndTime=""
                    startTime={0}
                    endTime={0}
                    captureTime={0}
                    s3Key=""
                    s3Filename=""
                    videoTitle={openClip.title}
                    hideTitlePrefixInHeader={true}
                />
            )}
        </>
    );
};
